import { RiCloseLine } from "@remixicon/react";
import { cn } from "cn";
import type { ComponentProps, ReactNode, Ref } from "react";

/**
 * Плотная метка 22 px: приглушённая подпись, значение и, если передан
 * `onRemove`, крестик. Доступное имя крестика обязательно — без него кнопка
 * из одной иконки нема для скринридера.
 */
type TagRemoveProps =
  | { onRemove?: undefined; removeLabel?: undefined; removeRef?: undefined }
  | {
      onRemove: () => void;
      removeLabel: string;
      /** Ссылка на кнопку-крестик: экран переводит на неё фокус при снятии соседней метки. */
      removeRef?: Ref<HTMLButtonElement>;
    };

type TagProps = Omit<ComponentProps<"span">, "children"> &
  TagRemoveProps & {
    label?: ReactNode;
    children: ReactNode;
  };

function Tag({ label, children, onRemove, removeLabel, removeRef, className, ...props }: TagProps) {
  return (
    <span
      data-slot="tag"
      className={cn(
        "inline-flex h-[22px] shrink-0 items-center gap-1 rounded-sm bg-secondary text-xs whitespace-nowrap text-foreground",
        onRemove ? "pr-1 pl-2" : "px-2",
        className,
      )}
      {...props}
    >
      {label !== undefined && <span className="text-muted-foreground">{label}</span>}
      {label !== undefined && " "}
      <span className="font-medium text-foreground">{children}</span>
      {onRemove && (
        <button
          ref={removeRef}
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-sm text-muted-foreground outline-none hover:bg-foreground/8 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <RiCloseLine className="size-3" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

export type { TagProps };
export { Tag };
